// The store's go-live checklist (Stephanie 2026-10-22): what the rep sees
// before sending a store to the client for sign-off. Every line must be ok
// to send (the API checks the same things). Pure — the offline gate runs it.
import type { InvoiceTerms, PaymentOption, StoreDetail } from "./companyStores";

/** The words for a payment option / invoice terms (PAYMENT_LABEL, TERMS_LABEL). */
export interface ChecklistWords {
  pay: (p: PaymentOption) => string;
  terms: (t: InvoiceTerms) => string;
}

/** One line of the store's go-live checklist (Stephanie 2026-10-22). */
export interface ChecklistItem {
  label: string;
  ok: boolean;
  /** What's set, or what's missing, in plain words. */
  detail: string;
}

const ymdWords = (d: string) => {
  const [y, m, day] = d.split("-").map(Number);
  return new Date(Date.UTC(y!, m! - 1, day!)).toLocaleDateString("en-US", { timeZone: "UTC", month: "short", day: "numeric", year: "numeric" });
};

/** Pure: the go-live checklist the rep sees before sending a store for
 *  sign-off. Every item must be ok to send. `today` = YYYY-MM-DD. */
export function storeChecklist(s: StoreDetail, today: string, words: ChecklistWords): ChecklistItem[] {
  const active = s.products.filter((p) => p.active);
  const unpriced = active.filter((p) => p.headline === null).length;
  const list = s.shipToList ?? [];
  const oneMethod = s.paymentOptions.length === 1 && s.paymentOptions[0] !== "employee_card";
  const payWords = s.paymentOptions.map((p) => (p === "invoice" ? `${words.pay(p)}${s.invoiceTerms ? ` (${words.terms(s.invoiceTerms)})` : ""}` : words.pay(p))).join(", ");
  const items: ChecklistItem[] = [
    { label: "Products", ok: active.length > 0, detail: active.length ? `${active.length} product${active.length === 1 ? "" : "s"}` : "Add at least one product" },
    { label: "Every product has a price", ok: active.length > 0 && unpriced === 0, detail: unpriced ? `${unpriced} without a price — take ${unpriced === 1 ? "it" : "them"} out until priced` : active.length ? "Yes" : "—" },
    { label: "Logo", ok: !!s.logoFileId, detail: s.logoFileId ? "Added" : "Add the store's logo" },
    { label: "Where orders ship", ok: true, detail: list.length === 0 ? "Each person enters their own address" : list.length === 1 ? `All orders ship to ${list[0]!.street1}, ${list[0]!.city}` : `${list.length} store addresses to pick from` },
    list.length
      ? { label: "Billing", ok: oneMethod && !!s.billingContactId, detail: !oneMethod ? "Pick Invoice or Company card" : !s.billingContactId ? `${payWords} — pick the billing contact` : `${payWords}, billing contact set` }
      : { label: "How people pay", ok: s.paymentOptions.length > 0, detail: s.paymentOptions.length ? payWords : "Choose at least one way to pay" },
    { label: "Shipping charge", ok: true, detail: s.shippingRanges?.length ? `${s.shippingRanges.length} set range${s.shippingRanges.length === 1 ? "" : "s"}` : "Worked out from each address" },
    { label: "End date", ok: !s.endsOn || s.endsOn >= today, detail: !s.endsOn ? "No end date" : s.endsOn >= today ? `Last day to order: ${ymdWords(s.endsOn)}` : `Already passed (${ymdWords(s.endsOn)}) — pick a new date` },
  ];
  return items;
}
